import { getInfoLocalStorage, request } from '../redux/api/config'

// Access-control check backed only by the API endpoints.
// This function validates access based on server-side permissions.

const resolveKeyFromPath = (pathname) => {
    const clean = (pathname || '').replace(/^\//, '')
    if (!clean) return null
    // e.g. '/operacional/asa' => 'operacional/asa'
    return clean
}

// Try to infer a feature from the URL path (group-level)
const inferFeatureCandidatesFromPath = (pathname) => {
    const path = (pathname || '').replace(/^\//, '')
    const first = path.split('/')[0]
    const candidates = []
    switch (first) {
        case 'clientes':
        case 'cliente':
            candidates.push('ui_clientes')
            break
        case 'aprovacoes':
            // try both with and without diacritics
            candidates.push('ui_aprovações', 'ui_aprovacoes')
            break
        case 'contratos':
        case 'contrato':
            candidates.push('ui_contratos')
            break
        case 'comissoes':
        case 'comissões':
            candidates.push('ui_comissões', 'ui_comissoes')
            break
        case 'saques':
            candidates.push('ui_saques')
            break
        case 'automacoes':
            candidates.push('ui_automacoes')
            break
        case 'operacional':
            candidates.push('ui_operacional')
            break
        default:
            break
    }
    return candidates
}

export const checkAccess = async (resourceKey, pathname) => {
    const stored = getInfoLocalStorage()
    const token = stored?.token

    // if there is no token, deny by default
    if (!token) return false

    const key = resourceKey || resolveKeyFromPath(pathname)

    // If a resourceKey was explicitly provided, try feature-based check first
    if (resourceKey) {
        const featureCandidates = [resourceKey]
        if (resourceKey.includes('-')) featureCandidates.push(resourceKey.replace(/-/g, '_'))
        if (resourceKey.includes('_')) featureCandidates.push(resourceKey.replace(/_/g, '-'))
        // common aliases for permission management
        if (resourceKey === 'permission_management') {
            featureCandidates.push('admin-permissions/manage', 'admin_permissions_manage', 'admin_permission_manage', 'admin_permissions_menage', 'admin_permission_menage')
        }
        for (const candidate of featureCandidates) {
            try {
                const res = await request('admin-permissions/check-feature', 'POST', { feature_name: candidate })
                if (typeof res?.data?.allowed === 'boolean' && res.data.allowed === true) return true
                if (typeof res?.allowed === 'boolean' && res.allowed === true) return true
                // if explicitly denied, do not return yet; fall through to URL checks
            } catch (_) {}
        }
    }

    // 2) Fallback to URL-based check (covers dynamic: '/operacional/*')
    try {
        const rawUrl = `/${resolveKeyFromPath(pathname || key)}`
        // Tenta padrão exato e variações com wildcard para :id
        const candidates = [rawUrl]
        const wildcard = rawUrl.replace(/\/[\w-]+(?=\/|$)/g, (m, idx) => {
            // só substitui segmentos que parecem ids numéricos ou alfanuméricos
            if (/^\/[0-9a-zA-Z_-]+$/.test(m)) return '/*'
            return m
        })
        if (!candidates.includes(wildcard)) candidates.push(wildcard)
        for (const u of candidates) {
            const res = await request('admin-permissions/check-access', 'POST', { url: u, method: 'GET' })
            if (typeof res?.data?.allowed === 'boolean') return res.data.allowed
            if (typeof res?.allowed === 'boolean') return res.allowed
        }
    } catch (_) {}

    // 2b) If route matches cliente/:id/permissoes, try normalized patterns
    try {
        const raw = `/${resolveKeyFromPath(pathname || key)}`
        const isUserPerms = /\/cliente\/[^/]+\/permissoes$/i.test(raw)
        if (isUserPerms) {
            for (const variant of ['/cliente/:id/permissoes', '/cliente/*/permissoes']) {
                try {
                    const res = await request('admin-permissions/check-access', 'POST', { url: variant, method: 'GET' })
                    if (typeof res?.data?.allowed === 'boolean') return res.data.allowed
                    if (typeof res?.allowed === 'boolean') return res.allowed
                } catch (_) {}
            }
        }
        // Operacional dinâmico
        const isOperacional = /\/operacional\/.+/.test(raw)
        if (isOperacional) {
            for (const variant of ['/operacional/:id', '/operacional/*']) {
                try {
                    const res = await request('admin-permissions/check-access', 'POST', { url: variant, method: 'GET' })
                    if (typeof res?.data?.allowed === 'boolean') return res.data.allowed
                    if (typeof res?.allowed === 'boolean') return res.allowed
                } catch (_) {}
            }
        }
        // Automações dinâmico
        const isAutomacoes = /\/automacoes\/.+/.test(raw)
        if (isAutomacoes) {
            for (const variant of ['/automacoes/:id', '/automacoes/*', '/automacoes/nova']) {
                try {
                    const res = await request('admin-permissions/check-access', 'POST', { url: variant, method: 'GET' })
                    if (typeof res?.data?.allowed === 'boolean') return res.data.allowed
                    if (typeof res?.allowed === 'boolean') return res.allowed
                } catch (_) {}
            }
        }
    } catch (_) {}

    // 2c) As a last attempt, check group-level features inferred from the path
    try {
        const raw = `/${resolveKeyFromPath(pathname || key)}`
        const firstSeg = raw.replace(/^\//, '').split('/')[0]
        // Skip feature checks for operacional pages; rely on URL-based rules
        if (firstSeg !== 'operacional') {
            const features = inferFeatureCandidatesFromPath(pathname || key)
            for (const f of features) {
                try {
                    const res = await request('admin-permissions/check-feature', 'POST', { feature_name: f })
                    if (typeof res?.data?.allowed === 'boolean' && res.data.allowed === true) return true
                    if (typeof res?.allowed === 'boolean' && res.allowed === true) return true
                } catch (_) {}
            }
        }
    } catch (_) {}

    // 3) Extra fallback: if it is a dynamic operacional path, try 'operacional_dynamic'
    if (key && key.startsWith('operacional/')) {
        try {
            const res = await request('admin-permissions/check-feature', 'POST', { feature_name: 'operacional_dynamic' })
            if (typeof res?.data?.allowed === 'boolean') return res.data.allowed
            if (typeof res?.allowed === 'boolean') return res.allowed
        } catch (_) {}
    }

    // 4) Default deny if no strategy confirmed access
    return false
}


